import { z } from "zod";
import type { ConsentGovernanceItem } from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import type { ConsentGovernanceItemWrite } from "src/api/client-api/consent/useManageConsentGovernanceAdmin";

export const CONSENT_ITEM_EDIT_CODE_PATTERN = /^[a-z0-9_-]+$/;

export const ConsentItemFormSchema = z.object({
    code: z
        .string()
        .trim()
        .min(1, "Code is required")
        .max(100, "Code cannot exceed 100 characters")
        .regex(CONSENT_ITEM_EDIT_CODE_PATTERN, "Use lowercase letters, digits, underscores, and hyphens only"),
    name: z.string().trim().max(255, "Name cannot exceed 255 characters"),
    description: z.string().optional(),
    category: z.string().max(100, "Category cannot exceed 100 characters").optional(),
    sortOrder: z.number().min(0, "Sort order must be positive"),
    active: z.boolean(),
});

export type ConsentItemFormData = z.infer<typeof ConsentItemFormSchema>;

export type ConsentItemLocalizedDraft = {
    description: string;
    dirty: boolean;
    initialized: boolean;
    name: string;
};

export const normalizeOptionalString = (value?: string) => {
    const normalized = value?.trim();

    return normalized && normalized.length > 0 ? normalized : null;
};

export const getConsentItemPayloadName = (
    draft: Pick<ConsentItemLocalizedDraft, "description" | "name">,
    fallbackName?: string,
) => {
    const draftName = draft.name.trim();
    const normalizedFallbackName = fallbackName?.trim();

    return draftName.length > 0 ? draftName : (normalizedFallbackName ?? "");
};

export const canSaveConsentItemLocalizedDraft = (
    draft: Pick<ConsentItemLocalizedDraft, "description" | "name">,
    fallbackName?: string,
) =>
    getConsentItemPayloadName(draft, fallbackName).length > 0 &&
    (draft.name.trim().length > 0 || draft.description.trim().length > 0);

export const getConsentItemGlobalSyncKey = (
    consentItem: ConsentGovernanceItem | undefined,
    itemType: "document" | "opt_in",
) =>
    consentItem ?
        [
            consentItem.id,
            itemType,
            consentItem.code,
            consentItem.category ?? "",
            consentItem.active ? "active" : "inactive",
            consentItem.sortOrder,
        ].join("|")
    :   "new";

export const buildConsentItemPayload = (
    values: ConsentItemFormData,
    draft: Pick<ConsentItemLocalizedDraft, "description" | "name">,
    itemType: "document" | "opt_in",
    fallbackName?: string,
): ConsentGovernanceItemWrite => ({
    code: values.code.trim(),
    type: itemType,
    name: getConsentItemPayloadName(draft, fallbackName),
    description: normalizeOptionalString(draft.description),
    required: itemType === "document",
    active: values.active,
    sortOrder: values.sortOrder,
    category: normalizeOptionalString(values.category),
});
